<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Vue3ChartJs from '@j-t-mcc/vue3-chartjs'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

mapboxgl.accessToken =
    'pk.eyJ1IjoiYWFyY2lsYTE4IiwiYSI6ImNsM2V6YWdscjBuZDMzY3A3M2FtMDl6YWgifQ.adh5HQ_qhIy3cjG1a-pSAQ'

const props = defineProps({
    regionalPrices: { type: Array, default: () => [] },
})

const mapContainer = ref(null)
const chartRef = ref(null)
const selectedSeries = ref('')
const selectedRegion = ref('')
const mapReady = ref(false)

let map = null
let popup = null

const unitLabels = {
    g: 'gramo', kg: 'kg', lb: 'libra', arroba: 'arroba', quintal: 'quintal', ton: 'tonelada',
    unidad: 'unidad', docena: 'docena', ciento: 'ciento', racimo: 'racimo', manojo: 'manojo',
    atado: 'atado', bandeja: 'bandeja', bolsa: 'bolsa', paquete: 'paquete', caja: 'caja',
    canastilla: 'canastilla', bulto: 'bulto', saco: 'saco', litro: 'litro', galon: 'galón',
}
function unitLabel(unit) { return unitLabels[unit] || unit || 'unidad' }

const seriesKey = price => `${price.product_form || 'Producto comparable'}|||${price.unit}`
const seriesOptions = computed(() => {
    const options = new Map()
    props.regionalPrices.forEach(price => {
        const key = seriesKey(price)
        if (!options.has(key)) {
            options.set(key, {
                key,
                label: `${price.product_form || 'Producto comparable'} · por ${unitLabel(price.unit)}`,
            })
        }
    })
    return [...options.values()]
})

watch(seriesOptions, options => {
    if (!options.some(option => option.key === selectedSeries.value)) {
        selectedSeries.value = options.length ? options[0].key : ''
    }
}, { immediate: true })

const filteredPrices = computed(() => props.regionalPrices.filter(price => seriesKey(price) === selectedSeries.value))

const median = values => {
    const sorted = values.map(Number).filter(Number.isFinite).sort((left, right) => left - right)
    if (!sorted.length) return 0
    const middle = Math.floor(sorted.length / 2)
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

const regionalGroups = computed(() => {
    const groups = new Map()
    filteredPrices.value.forEach(price => {
        const key = `${price.country_code}|${price.region}`
        if (!groups.has(key)) {
            groups.set(key, { key, country: price.country, region: price.region, records: [] })
        }
        groups.get(key).records.push(price)
    })

    return [...groups.values()].map(group => {
        const values = group.records.map(record => Number(record.normalized_price_usd)).filter(Number.isFinite)
        const latitudes = group.records.map(record => Number(record.latitude)).filter(Number.isFinite)
        const longitudes = group.records.map(record => Number(record.longitude)).filter(Number.isFinite)
        const sources = new Set(group.records.map(record => record.source_name).filter(Boolean))
        const latestDate = group.records.map(record => record.observed_at).filter(Boolean).sort().reverse()[0]
        return {
            ...group,
            median: median(values),
            minimum: Math.min(...values),
            maximum: Math.max(...values),
            latitude: latitudes.reduce((sum, value) => sum + value, 0) / latitudes.length,
            longitude: longitudes.reduce((sum, value) => sum + value, 0) / longitudes.length,
            sourceCount: sources.size,
            recordCount: group.records.length,
            latestDate,
        }
    }).filter(group => Number.isFinite(group.latitude) && Number.isFinite(group.longitude))
      .sort((left, right) => right.median - left.median)
})

watch(regionalGroups, groups => {
    if (!groups.some(group => group.key === selectedRegion.value)) {
        selectedRegion.value = ''
    }
}, { deep: true })

const activeGroup = computed(() => regionalGroups.value.find(group => group.key === selectedRegion.value) || null)
const activeUnit = computed(() => filteredPrices.value[0] ? filteredPrices.value[0].unit : '')

const formatUsd = value => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 4,
}).format(Number(value))

const formatDate = value => value
    ? new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`))
    : 'Sin fecha'

const colorForValue = value => {
    const values = regionalGroups.value.map(group => group.median)
    const minimum = Math.min(...values)
    const maximum = Math.max(...values)
    if (maximum === minimum) return '#F3E022'
    const position = (value - minimum) / (maximum - minimum)
    if (position <= 0.33) return '#22A95A'
    if (position <= 0.66) return '#F3E022'
    return '#F2994A'
}

const chartData = {
    labels: [],
    datasets: [{
        label: 'Mediana regional',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 2,
        borderRadius: 7,
    }],
}

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    onClick: (event, elements) => {
        if (elements.length) selectRegion(regionalGroups.value[elements[0].index])
    },
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: context => `${formatUsd(context.raw)} / ${unitLabel(activeUnit.value)}`,
            },
        },
    },
    scales: {
        x: {
            beginAtZero: true,
            title: { display: true, text: 'Mediana regional en USD' },
            ticks: { callback: value => `$${value}` },
        },
        y: { grid: { display: false } },
    },
}

const refreshChart = () => {
    chartData.labels = regionalGroups.value.map(group => group.region)
    chartData.datasets[0].data = regionalGroups.value.map(group => group.median)
    chartData.datasets[0].backgroundColor = regionalGroups.value.map(group => group.key === selectedRegion.value ? '#03662E' : colorForValue(group.median))
    chartData.datasets[0].borderColor = regionalGroups.value.map(group => group.key === selectedRegion.value ? '#03662E' : '#ffffff')
    nextTick(() => chartRef.value && chartRef.value.update(250))
}

const geoJson = () => ({
    type: 'FeatureCollection',
    features: regionalGroups.value.map(group => ({
        type: 'Feature',
        properties: {
            key: group.key,
            country: group.country,
            region: group.region,
            median: group.median,
            minimum: group.minimum,
            maximum: group.maximum,
            sourceCount: group.sourceCount,
            recordCount: group.recordCount,
            latestDate: group.latestDate || '',
            color: colorForValue(group.median),
        },
        geometry: { type: 'Point', coordinates: [group.longitude, group.latitude] },
    })),
})

const fitRegions = (animate = true) => {
    if (!mapReady.value || !regionalGroups.value.length) return
    if (regionalGroups.value.length === 1) {
        const group = regionalGroups.value[0]
        map.easeTo({ center: [group.longitude, group.latitude], zoom: 6, duration: animate ? 600 : 0 })
        return
    }
    const coordinates = regionalGroups.value.map(group => [group.longitude, group.latitude])
    const bounds = coordinates.reduce(
        (result, coordinate) => result.extend(coordinate),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
    )
    map.fitBounds(bounds, { padding: 60, maxZoom: 7, duration: animate ? 600 : 0 })
}

const refreshMap = (fit = true) => {
    if (!mapReady.value) return
    const source = map.getSource('regional-price-points')
    if (source) source.setData(geoJson())
    map.setFilter('regional-price-selected', ['==', ['get', 'key'], selectedRegion.value || '__none__'])
    if (fit) fitRegions()
}

const popupContent = properties => {
    const container = document.createElement('div')
    container.className = 'regional-price-popup'
    const title = document.createElement('strong')
    title.textContent = `${properties.region}, ${properties.country}`
    container.appendChild(title)
    const rows = [
        `Mediana: ${formatUsd(properties.median)} / ${unitLabel(activeUnit.value)}`,
        `Rango: ${formatUsd(properties.minimum)} – ${formatUsd(properties.maximum)}`,
        `${properties.sourceCount} ${Number(properties.sourceCount) === 1 ? 'fuente' : 'fuentes'} · ${properties.recordCount} ${Number(properties.recordCount) === 1 ? 'dato' : 'datos'}`,
        `Actualizado: ${formatDate(properties.latestDate)}`,
    ]
    rows.forEach(text => {
        const row = document.createElement('span')
        row.textContent = text
        container.appendChild(row)
    })
    return container
}

const selectRegion = group => {
    if (!group) return
    selectedRegion.value = group.key
    refreshChart()
    refreshMap(false)
}

const showPopup = event => {
    const feature = event.features && event.features[0]
    if (!feature) return
    const group = regionalGroups.value.find(item => item.key === feature.properties.key)
    selectRegion(group)
    if (popup) popup.remove()
    popup = new mapboxgl.Popup({ closeButton: true, offset: 14 })
        .setLngLat(event.lngLat)
        .setDOMContent(popupContent(feature.properties))
        .addTo(map)
}

watch(regionalGroups, () => {
    refreshChart()
    refreshMap()
}, { deep: true })

watch(selectedRegion, () => {
    refreshChart()
    refreshMap(false)
})

onMounted(() => {
    map = new mapboxgl.Map({
        container: mapContainer.value,
        style: 'mapbox://styles/mapbox/light-v10',
        center: [-74.2973, 4.5709],
        zoom: 4.1,
        cooperativeGestures: false,
    })
    map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    map.addControl(new mapboxgl.FullscreenControl(), 'top-right')
    map.on('load', () => {
        map.addSource('regional-price-points', { type: 'geojson', data: geoJson() })
        map.addLayer({
            id: 'regional-price-halo', type: 'circle', source: 'regional-price-points',
            paint: { 'circle-radius': 18, 'circle-color': '#ffffff', 'circle-opacity': 0.9 },
        })
        map.addLayer({
            id: 'regional-price-circles', type: 'circle', source: 'regional-price-points',
            paint: {
                'circle-radius': ['interpolate', ['linear'], ['get', 'recordCount'], 1, 10, 10, 18],
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#03662E',
                'circle-stroke-width': 2,
                'circle-opacity': 0.92,
            },
        })
        map.addLayer({
            id: 'regional-price-selected', type: 'circle', source: 'regional-price-points',
            filter: ['==', ['get', 'key'], '__none__'],
            paint: { 'circle-radius': 23, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#03662E', 'circle-stroke-width': 5 },
        })
        map.addLayer({
            id: 'regional-price-labels', type: 'symbol', source: 'regional-price-points', minzoom: 4.5,
            layout: {
                'text-field': ['get', 'region'],
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'text-offset': [0, 1.8],
                'text-anchor': 'top',
            },
            paint: { 'text-color': '#063A20', 'text-halo-color': '#ffffff', 'text-halo-width': 2 },
        })
        map.on('click', 'regional-price-circles', showPopup)
        map.on('mouseenter', 'regional-price-circles', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'regional-price-circles', () => { map.getCanvas().style.cursor = '' })
        mapReady.value = true
        fitRegions(false)
    })
    refreshChart()
})

onBeforeUnmount(() => {
    if (popup) popup.remove()
    if (map) map.remove()
    map = null
})
</script>

<template>
    <section class="mt-10 overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
        <header class="p-5 border-b md:p-7 bg-light_yellow border-light_gray">
            <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                    <p class="text-sm font-bold tracking-wider uppercase text-enriqueto_green">Precios por región</p>
                    <h2 class="mt-1 text-2xl font-bold text-enriqueto_green">Comparación geográfica</h2>
                    <p class="max-w-3xl mt-2 text-sm leading-relaxed text-gray">
                        Referencias informativas de distintas fuentes. No son ofertas de productores y se comparan únicamente datos con la misma unidad y presentación.
                    </p>
                </div>
                <label v-if="seriesOptions.length" class="block md:w-80">
                    <span class="text-xs font-bold tracking-wider uppercase text-enriqueto_green">Serie comparable</span>
                    <select v-model="selectedSeries" class="w-full px-4 py-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option v-for="option in seriesOptions" :key="option.key" :value="option.key">{{ option.label }}</option>
                    </select>
                </label>
            </div>
        </header>

        <div class="relative">
            <div class="grid lg:grid-cols-2">
                <div class="relative border-b lg:border-b-0 lg:border-r border-light_gray">
                    <div class="absolute z-10 flex flex-wrap gap-3 regional-map-legend">
                        <span><i class="bg-low"></i>Menor</span>
                        <span><i class="bg-middle"></i>Intermedio</span>
                        <span><i class="bg-high"></i>Mayor</span>
                    </div>
                    <div ref="mapContainer" class="regional-price-map"></div>
                </div>
                <div class="p-5 md:p-7">
                    <div v-if="regionalGroups.length" class="regional-price-chart">
                        <vue3-chart-js ref="chartRef" id="regional-price-comparison" type="bar" :data="chartData" :options="chartOptions" />
                    </div>
                    <div v-else class="flex items-center justify-center h-full min-h-80">
                        <div class="max-w-md text-center">
                            <span class="text-4xl material-symbols-outlined text-light_green">travel_explore</span>
                            <h3 class="mt-3 text-lg font-bold text-enriqueto_green">Aún no hay precios regionales comparables</h3>
                            <p class="mt-2 text-sm leading-relaxed text-gray">Cuando se agreguen fuentes con región, fecha, unidad y presentación compatibles, aparecerán aquí sin alterar el precio internacional ni las ofertas.</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="activeGroup" class="grid gap-4 p-5 border-t md:grid-cols-4 md:p-7 border-light_gray bg-light_yellow">
                <div><p class="text-xs text-gray">Región seleccionada</p><p class="mt-1 font-bold text-enriqueto_green">{{ activeGroup.region }}, {{ activeGroup.country }}</p></div>
                <div><p class="text-xs text-gray">Precio mediano</p><p class="mt-1 font-bold">{{ formatUsd(activeGroup.median) }} / {{ unitLabel(activeUnit) }}</p></div>
                <div><p class="text-xs text-gray">Rango observado</p><p class="mt-1 font-bold">{{ formatUsd(activeGroup.minimum) }} – {{ formatUsd(activeGroup.maximum) }}</p></div>
                <div><p class="text-xs text-gray">Cobertura</p><p class="mt-1 font-bold">{{ activeGroup.sourceCount }} {{ activeGroup.sourceCount === 1 ? 'fuente' : 'fuentes' }} · {{ formatDate(activeGroup.latestDate) }}</p></div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.regional-price-map {
    height: 500px;
    min-height: 420px;
}

.regional-price-chart {
    height: 450px;
}

.regional-map-legend {
    top: 1rem;
    left: 1rem;
    padding: 0.7rem 0.9rem;
    background: rgba(255, 255, 255, 0.94);
    border-radius: 0.8rem;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.regional-map-legend span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #4f4f4f;
    font-size: 0.72rem;
    font-weight: 700;
}

.regional-map-legend i {
    display: inline-block;
    width: 0.75rem;
    height: 0.75rem;
    border: 1px solid #03662E;
    border-radius: 999px;
}

.bg-low { background: #22A95A; }
.bg-middle { background: #F3E022; }
.bg-high { background: #F2994A; }

:deep(.regional-price-popup) {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 220px;
    padding: 0.25rem;
}

:deep(.regional-price-popup strong) {
    color: #03662E;
    font-size: 1rem;
    margin-bottom: 0.2rem;
}

:deep(.regional-price-popup span) {
    color: #4b5563;
    font-size: 0.78rem;
}

@media (max-width: 640px) {
    .regional-price-map {
        height: 440px;
        min-height: 400px;
    }
    .regional-price-chart { height: 390px; }
}
</style>
